import { Extension } from "@tiptap/core";

export interface LineHeightOptions {
  types: string[];
  defaultHeight: string | null;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    lineHeight: {
      setLineHeight: (height: string) => ReturnType;
      unsetLineHeight: () => ReturnType;
    };
  }
}

// Adds a `lineHeight` attribute to block nodes, rendered as inline
// `style="line-height: X"`. Same block-attribute shape as TextAlign — when
// unset, blocks inherit the line-height from CSS (.text-widget-content).
export const LineHeight = Extension.create<LineHeightOptions>({
  name: "lineHeight",

  addOptions() {
    return {
      types: ["paragraph", "heading"],
      defaultHeight: null,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: this.options.defaultHeight,
            parseHTML: (element) => element.style.lineHeight || this.options.defaultHeight,
            renderHTML: (attributes) =>
              attributes.lineHeight ? { style: `line-height: ${attributes.lineHeight}` } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      // .map (not .every) so every type is updated unconditionally — .every
      // short-circuits on the first type that returns false (e.g. "heading"
      // when the cursor is in a paragraph), skipping the rest. Mirrors TextAlign.
      setLineHeight:
        (height) =>
        ({ commands }) =>
          this.options.types
            .map((type) => commands.updateAttributes(type, { lineHeight: height }))
            .some((applied) => applied),
      unsetLineHeight:
        () =>
        ({ commands }) =>
          this.options.types
            .map((type) => commands.resetAttributes(type, "lineHeight"))
            .some((applied) => applied),
    };
  },
});
